import React from "react";
import {
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    Controller,
    Control,
    FieldArrayWithId,
    FieldErrors,
    UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TendbeQRCode } from "@Components/TendbeQRCode/TendbeQRCode";
import {
    ChannelSettingsDialogStyles,
    PurpleSwitch,
} from "./ChannelSettingsDialog.styles";
import {
    ChannelSettingsFormData,
    MAX_COMPLETION_REDIRECT_URL_LENGTH,
    MAX_CTA_MSG_LENGTH,
    MAX_CUSTOM_LABEL_LENGTH,
    MAX_CUSTOM_TEXT_QUESTION_LENGTH,
    MAX_TITLE_LENGTH,
    MAX_VOTERS_VP_LENGTH,
} from "./ChannelSettingsDialog.types";
import { resolveRedirectTarget } from "@Utils/redirectUrl";
import {
    getCharCount,
    UPGRADE_TOOLTIP_KEYS,
} from "./ChannelSettingsDialog.helpers";
import {
    LockedSetting,
    PremiumSettingLabel,
} from "./ChannelSettingsDialog.components";

type DemographicFieldName =
    | "collectAge"
    | "collectGender"
    | "collectCountry"
    | "collectNationality"
    | "collectSector"
    | "collectIndustry"
    | "collectProfession"
    | "collectRole"
    | "collectOrganizationType";

const demographicOptions: Array<{
    name: DemographicFieldName;
    translationKey: string;
}> = [
    { name: "collectAge", translationKey: "age" },
    { name: "collectGender", translationKey: "gender" },
    { name: "collectCountry", translationKey: "country" },
    { name: "collectNationality", translationKey: "nationality" },
    { name: "collectSector", translationKey: "sector" },
    { name: "collectIndustry", translationKey: "industry" },
    { name: "collectProfession", translationKey: "profession" },
    { name: "collectRole", translationKey: "role" },
    { name: "collectOrganizationType", translationKey: "organization_type" },
];

interface GeneralSettingsSectionProps {
    allowCommentsChecked: boolean;
    control: Control<ChannelSettingsFormData>;
    displayProfilePictureChecked: boolean;
    errors: FieldErrors<ChannelSettingsFormData>;
    handleAllowCommentsToggle: () => void;
    handleChangeEmailRequired: () => void;
    handleDisplayProfilePictureToggle: () => void;
    isEmailRequiredChecked: boolean;
    isSubChannel: boolean;
    register: UseFormRegister<ChannelSettingsFormData>;
}

export const GeneralSettingsSection: React.FC<GeneralSettingsSectionProps> = ({
    allowCommentsChecked,
    control,
    displayProfilePictureChecked,
    errors,
    handleAllowCommentsToggle,
    handleChangeEmailRequired,
    handleDisplayProfilePictureToggle,
    isEmailRequiredChecked,
    isSubChannel,
    register,
}) => {
    const { t } = useTranslation();

    return (
        <>
            <Grid item xs={12}>
                <Controller
                    name="title"
                    control={control}
                    rules={{
                        required: true,
                        minLength: 3,
                        maxLength: MAX_TITLE_LENGTH,
                    }}
                    render={({
                        field: { value = "", onChange, onBlur, ref },
                    }) => (
                        <TextField
                            error={!!(errors && errors.title)}
                            autoFocus={false}
                            label={t("dialogs.channel_settings.title")}
                            fullWidth
                            required
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            inputRef={ref}
                            helperText={`${getCharCount(
                                value
                            )}/${MAX_TITLE_LENGTH}`}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12}>
                <Controller
                    name="description"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    render={({
                        field: { value = "", onChange, onBlur, ref },
                    }) => (
                        <TextField
                            label={t("dialogs.channel_settings.description")}
                            variant="outlined"
                            fullWidth
                            multiline
                            maxRows={4}
                            minRows={4}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            inputRef={ref}
                            placeholder={t(
                                "dialogs.channel_settings.description"
                            )}
                        />
                    )}
                />
            </Grid>
            {!isSubChannel && (
                <>
                    <Grid item xs={12}>
                        <Controller
                            name="votersVp"
                            control={control}
                            rules={{
                                maxLength: MAX_VOTERS_VP_LENGTH,
                            }}
                            render={({
                                field: { value = "", onChange, onBlur, ref },
                            }) => (
                                <TextField
                                    label={t(
                                        "dialogs.channel_settings.value_proposition"
                                    )}
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    maxRows={2}
                                    minRows={2}
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    inputRef={ref}
                                    placeholder={t(
                                        "dialogs.channel_settings.value_proposition"
                                    )}
                                    helperText={`${getCharCount(
                                        value
                                    )}/${MAX_VOTERS_VP_LENGTH}`}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="ctaMsg"
                            control={control}
                            rules={{
                                maxLength: MAX_CTA_MSG_LENGTH,
                            }}
                            render={({
                                field: { value = "", onChange, onBlur, ref },
                            }) => (
                                <TextField
                                    label={t(
                                        "dialogs.channel_settings.call_to_action"
                                    )}
                                    variant="outlined"
                                    fullWidth
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    inputRef={ref}
                                    placeholder={t(
                                        "dialogs.channel_settings.call_to_action"
                                    )}
                                    helperText={`${getCharCount(
                                        value
                                    )}/${MAX_CTA_MSG_LENGTH}`}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            {t("dialogs.channel_settings.allow_comments")}
                            <PurpleSwitch
                                inputRef={register("allowComments").ref}
                                name="allowComments"
                                checked={allowCommentsChecked}
                                onChange={handleAllowCommentsToggle}
                                size="medium"
                                inputProps={{
                                    "aria-label": t(
                                        "dialogs.channel_settings.allow_comments_toggle_aria"
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            {t(
                                "dialogs.channel_settings.display_profile_picture"
                            )}
                            <PurpleSwitch
                                inputRef={register("displayProfilePicture").ref}
                                name="displayProfilePicture"
                                checked={displayProfilePictureChecked}
                                onChange={handleDisplayProfilePictureToggle}
                                size="medium"
                                inputProps={{
                                    "aria-label": t(
                                        "dialogs.channel_settings.display_profile_picture_toggle_aria"
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            {t("dialogs.channel_settings.ask_guest_email")}
                            <PurpleSwitch
                                inputRef={register("isEmailRequired").ref}
                                name="isEmailRequired"
                                checked={isEmailRequiredChecked}
                                onChange={handleChangeEmailRequired}
                                size="medium"
                                inputProps={{
                                    "aria-label": t(
                                        "dialogs.channel_settings.ask_guest_email_toggle_aria"
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
        </>
    );
};

interface DemographicsSectionProps {
    control: Control<ChannelSettingsFormData>;
    demographicsToggled: boolean;
    handleDemographicsToggled: () => void;
    isAdvancedDataCollectionLocked: boolean;
    register: UseFormRegister<ChannelSettingsFormData>;
}

export const DemographicsSection: React.FC<DemographicsSectionProps> = ({
    control,
    demographicsToggled,
    handleDemographicsToggled,
    isAdvancedDataCollectionLocked,
    register,
}) => {
    const { t } = useTranslation();

    return (
        <Grid item xs={12}>
            <LockedSetting
                locked={isAdvancedDataCollectionLocked}
                tooltip={t(UPGRADE_TOOLTIP_KEYS.collectDemographics)}
            >
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <PremiumSettingLabel
                        label={t("dialogs.channel_settings.collect_demo")}
                        locked={isAdvancedDataCollectionLocked}
                    />
                    <PurpleSwitch
                        inputRef={register("isDemographic").ref}
                        name="isDemographic"
                        checked={demographicsToggled}
                        onChange={
                            isAdvancedDataCollectionLocked
                                ? undefined
                                : handleDemographicsToggled
                        }
                        disabled={isAdvancedDataCollectionLocked}
                        size="medium"
                        inputProps={{
                            "aria-label": t(
                                "dialogs.channel_settings.collect_demographics_toggle_aria"
                            ),
                        }}
                    />
                </Grid>
            </LockedSetting>
            {((demographicsToggled && !isAdvancedDataCollectionLocked) ||
                isAdvancedDataCollectionLocked) && (
                <LockedSetting
                    locked={isAdvancedDataCollectionLocked}
                    tooltip={t(UPGRADE_TOOLTIP_KEYS.collectDemographics)}
                >
                    <Grid container spacing={1}>
                        {demographicOptions.map((option) => (
                            <Grid item xs={12} sm={6} key={option.name}>
                                <Controller
                                    name={option.name}
                                    control={control}
                                    render={({
                                        field: { value, onChange },
                                    }) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    checked={!!value}
                                                    disabled={
                                                        isAdvancedDataCollectionLocked
                                                    }
                                                    onChange={(event) => {
                                                        if (
                                                            isAdvancedDataCollectionLocked
                                                        ) {
                                                            return;
                                                        }
                                                        onChange(
                                                            event.target.checked
                                                        );
                                                    }}
                                                />
                                            }
                                            label={
                                                <PremiumSettingLabel
                                                    label={t(
                                                        `dialogs.channel_settings.demographics.${option.translationKey}`
                                                    )}
                                                    locked={
                                                        isAdvancedDataCollectionLocked
                                                    }
                                                />
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </LockedSetting>
            )}
        </Grid>
    );
};

interface CustomVariablesSectionProps {
    classes: ChannelSettingsDialogStyles;
    control: Control<ChannelSettingsFormData>;
    customVariableFields: FieldArrayWithId<
        ChannelSettingsFormData,
        "customVariables",
        "id"
    >[];
    customVariablesToggled: boolean;
    denormalizeOptions: (chips: string[]) => string;
    errors: FieldErrors<ChannelSettingsFormData>;
    handleCustomVariablesToggle: () => void;
    handleRemoveCustomVariable: (index: number) => void;
    isAdvancedDataCollectionLocked: boolean;
    normalizeOptions: (raw?: string | string[]) => string[];
    register: UseFormRegister<ChannelSettingsFormData>;
    validateCustomVariableLabel: (value?: string) => true | string;
    validateUniqueCustomVariableLabel: (
        value: string | undefined,
        index: number
    ) => true | string;
    watchedCustomVariables?: Array<{
        label?: string;
        options?: string;
    }>;
}

export const CustomVariablesSection: React.FC<CustomVariablesSectionProps> = ({
    classes,
    control,
    customVariableFields,
    customVariablesToggled,
    denormalizeOptions,
    errors,
    handleCustomVariablesToggle,
    handleRemoveCustomVariable,
    isAdvancedDataCollectionLocked,
    normalizeOptions,
    register,
    validateCustomVariableLabel,
    validateUniqueCustomVariableLabel,
    watchedCustomVariables,
}) => {
    const { t } = useTranslation();

    return (
        <Grid item xs={12}>
            <LockedSetting
                locked={isAdvancedDataCollectionLocked}
                tooltip={t(UPGRADE_TOOLTIP_KEYS.collectCustomData)}
            >
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <PremiumSettingLabel
                        label={t("dialogs.channel_settings.collect_custom")}
                        locked={isAdvancedDataCollectionLocked}
                    />
                    <PurpleSwitch
                        checked={customVariablesToggled}
                        onChange={
                            isAdvancedDataCollectionLocked
                                ? undefined
                                : handleCustomVariablesToggle
                        }
                        disabled={isAdvancedDataCollectionLocked}
                        size="medium"
                        inputProps={{
                            "aria-label": t(
                                "dialogs.channel_settings.custom_variables_toggle_aria"
                            ),
                        }}
                    />
                </Grid>
            </LockedSetting>
            {customVariablesToggled && (
                <LockedSetting
                    locked={isAdvancedDataCollectionLocked}
                    tooltip={t(UPGRADE_TOOLTIP_KEYS.collectCustomData)}
                >
                    <Box sx={classes.customSection}>
                        {customVariableFields.map((field, index) => {
                            const labelValue =
                                watchedCustomVariables?.[index]?.label ??
                                field.label ??
                                "";
                            const labelLength = labelValue?.length ?? 0;
                            const {
                                ref: customVariableLabelRef,
                                ...customVariableLabelField
                            } = register(`customVariables.${index}.label`, {
                                maxLength: {
                                    value: MAX_CUSTOM_LABEL_LENGTH,
                                    message: t(
                                        "dialogs.channel_settings.custom_variable_label_error",
                                        {
                                            max: MAX_CUSTOM_LABEL_LENGTH,
                                        }
                                    ),
                                },
                                validate: {
                                    reserved: validateCustomVariableLabel,
                                    unique: (value) =>
                                        validateUniqueCustomVariableLabel(
                                            value,
                                            index
                                        ),
                                },
                            });
                            const labelError = (
                                errors?.customVariables?.[index] as
                                    | {
                                          label?: {
                                              message?: string;
                                          };
                                      }
                                    | undefined
                            )?.label?.message;

                            return (
                                <Box key={field.id} sx={classes.customRow}>
                                    <TextField
                                        {...customVariableLabelField}
                                        label={
                                            <PremiumSettingLabel
                                                label={t(
                                                    "dialogs.channel_settings.custom_variable_label"
                                                )}
                                                locked={
                                                    isAdvancedDataCollectionLocked
                                                }
                                            />
                                        }
                                        placeholder={t(
                                            "dialogs.channel_settings.custom_variable_placeholder"
                                        )}
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        minRows={1}
                                        maxRows={3}
                                        defaultValue={field.label}
                                        disabled={
                                            isAdvancedDataCollectionLocked
                                        }
                                        inputRef={customVariableLabelRef}
                                        inputProps={{
                                            maxLength: MAX_CUSTOM_LABEL_LENGTH,
                                        }}
                                        error={Boolean(labelError)}
                                        helperText={
                                            labelError ??
                                            t(
                                                "dialogs.channel_settings.custom_variable_label_helper",
                                                {
                                                    current: labelLength,
                                                    max: MAX_CUSTOM_LABEL_LENGTH,
                                                }
                                            )
                                        }
                                        sx={classes.labelField}
                                    />
                                    <Controller
                                        name={`customVariables.${index}.options`}
                                        control={control}
                                        defaultValue={field.options}
                                        render={({
                                            field: { value, onChange },
                                        }) => {
                                            const chips = normalizeOptions(
                                                value as string | undefined
                                            );
                                            return (
                                                <Autocomplete
                                                    sx={classes.optionsField}
                                                    multiple
                                                    freeSolo
                                                    disableClearable
                                                    disabled={
                                                        isAdvancedDataCollectionLocked
                                                    }
                                                    options={[]}
                                                    value={chips}
                                                    onChange={(_, next) => {
                                                        if (
                                                            isAdvancedDataCollectionLocked
                                                        ) {
                                                            return;
                                                        }
                                                        const normalized = next
                                                            .map((item) =>
                                                                typeof item ===
                                                                "string"
                                                                    ? item.trim()
                                                                    : ""
                                                            )
                                                            .filter(
                                                                (item) => item
                                                            );

                                                        const unique =
                                                            Array.from(
                                                                new Set(
                                                                    normalized
                                                                )
                                                            );

                                                        onChange(
                                                            denormalizeOptions(
                                                                unique
                                                            )
                                                        );
                                                    }}
                                                    renderTags={(
                                                        tagValue,
                                                        getTagProps
                                                    ) =>
                                                        tagValue.map(
                                                            (
                                                                option,
                                                                tagIndex
                                                            ) => {
                                                                const chipLabel =
                                                                    typeof option ===
                                                                    "string"
                                                                        ? option
                                                                        : "";
                                                                return (
                                                                    <Chip
                                                                        {...getTagProps(
                                                                            {
                                                                                index: tagIndex,
                                                                            }
                                                                        )}
                                                                        key={`${chipLabel}-${tagIndex}`}
                                                                        label={
                                                                            chipLabel
                                                                        }
                                                                        size="small"
                                                                        sx={
                                                                            classes.chip
                                                                        }
                                                                    />
                                                                );
                                                            }
                                                        )
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            fullWidth
                                                            multiline
                                                            minRows={1}
                                                            maxRows={4}
                                                            label={
                                                                <PremiumSettingLabel
                                                                    label={t(
                                                                        "dialogs.channel_settings.custom_variable_options_label"
                                                                    )}
                                                                    locked={
                                                                        isAdvancedDataCollectionLocked
                                                                    }
                                                                />
                                                            }
                                                            placeholder={t(
                                                                "dialogs.channel_settings.custom_variable_options_placeholder"
                                                            )}
                                                        />
                                                    )}
                                                />
                                            );
                                        }}
                                    />
                                    <IconButton
                                        aria-label={t(
                                            "dialogs.channel_settings.remove_custom_variable"
                                        )}
                                        onClick={() => {
                                            if (
                                                isAdvancedDataCollectionLocked
                                            ) {
                                                return;
                                            }
                                            handleRemoveCustomVariable(index);
                                        }}
                                        disabled={
                                            isAdvancedDataCollectionLocked
                                        }
                                        sx={classes.removeButton}
                                        size="small"
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>
                </LockedSetting>
            )}
        </Grid>
    );
};

interface CustomTextFieldSectionProps {
    channelCustomTextFieldQuestion?: string | null;
    control: Control<ChannelSettingsFormData>;
    customTextFieldToggled: boolean;
    handleCustomTextFieldToggle: () => void;
    isAdvancedDataCollectionLocked: boolean;
    register: UseFormRegister<ChannelSettingsFormData>;
    watchCustomTextFieldQuestion?: string | null;
}

export const CustomTextFieldSection: React.FC<CustomTextFieldSectionProps> = ({
    channelCustomTextFieldQuestion,
    control,
    customTextFieldToggled,
    handleCustomTextFieldToggle,
    isAdvancedDataCollectionLocked,
    register,
    watchCustomTextFieldQuestion,
}) => {
    const { t } = useTranslation();

    return (
        <Grid item xs={12}>
            <LockedSetting
                locked={isAdvancedDataCollectionLocked}
                tooltip={t(UPGRADE_TOOLTIP_KEYS.customTextField)}
            >
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <PremiumSettingLabel
                        label={t("dialogs.channel_settings.custom_text_field")}
                        locked={isAdvancedDataCollectionLocked}
                    />
                    <PurpleSwitch
                        inputRef={register("customTextFieldEnabled").ref}
                        name="customTextFieldEnabled"
                        checked={customTextFieldToggled}
                        onChange={
                            isAdvancedDataCollectionLocked
                                ? undefined
                                : handleCustomTextFieldToggle
                        }
                        disabled={isAdvancedDataCollectionLocked}
                        size="medium"
                        inputProps={{
                            "aria-label": t(
                                "dialogs.channel_settings.custom_text_field_toggle_aria"
                            ),
                        }}
                    />
                </Grid>
            </LockedSetting>
            {customTextFieldToggled && (
                <LockedSetting
                    locked={isAdvancedDataCollectionLocked}
                    tooltip={t(UPGRADE_TOOLTIP_KEYS.customTextField)}
                >
                    <Box sx={{ mt: 2 }}>
                        <Controller
                            name="customTextFieldQuestion"
                            control={control}
                            rules={{
                                maxLength: MAX_CUSTOM_TEXT_QUESTION_LENGTH,
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={
                                        <PremiumSettingLabel
                                            label={t(
                                                "dialogs.channel_settings.custom_text_field"
                                            )}
                                            locked={
                                                isAdvancedDataCollectionLocked
                                            }
                                        />
                                    }
                                    placeholder={t(
                                        "dialogs.channel_settings.custom_text_field_placeholder"
                                    )}
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    disabled={isAdvancedDataCollectionLocked}
                                    minRows={2}
                                    maxRows={4}
                                    value={field.value ?? ""}
                                    inputProps={{
                                        maxLength:
                                            MAX_CUSTOM_TEXT_QUESTION_LENGTH,
                                    }}
                                    helperText={`${getCharCount(
                                        watchCustomTextFieldQuestion,
                                        channelCustomTextFieldQuestion
                                    )}/${MAX_CUSTOM_TEXT_QUESTION_LENGTH}`}
                                />
                            )}
                        />
                    </Box>
                </LockedSetting>
            )}
        </Grid>
    );
};

interface VotingRulesSectionProps {
    allowDoubleVoteChecked: boolean;
    channelDoubleVoteCooldownMinutes?: number | null;
    checked: boolean;
    classes: ChannelSettingsDialogStyles;
    control: Control<ChannelSettingsFormData>;
    handleAllowDoubleVoteToggle: () => void;
    handleChange: () => void;
    isAdvancedDataCollectionLocked: boolean;
    register: UseFormRegister<ChannelSettingsFormData>;
}

export const VotingRulesSection: React.FC<VotingRulesSectionProps> = ({
    allowDoubleVoteChecked,
    channelDoubleVoteCooldownMinutes,
    checked,
    classes,
    control,
    handleAllowDoubleVoteToggle,
    handleChange,
    isAdvancedDataCollectionLocked,
    register,
}) => {
    const { t } = useTranslation();

    return (
        <>
            <Grid item xs={12}>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {t("dialogs.channel_settings.hide_poll_results")}
                    <PurpleSwitch
                        inputRef={register("hideResults").ref}
                        name="hideResults"
                        checked={checked}
                        onChange={handleChange}
                        size="medium"
                        inputProps={{
                            "aria-label": t(
                                "dialogs.channel_settings.hide_results_toggle_aria"
                            ),
                        }}
                    />
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <LockedSetting
                    locked={isAdvancedDataCollectionLocked}
                    tooltip={t(UPGRADE_TOOLTIP_KEYS.allowDoubleVote)}
                >
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <PremiumSettingLabel
                            label={t(
                                "dialogs.channel_settings.allow_double_vote"
                            )}
                            locked={isAdvancedDataCollectionLocked}
                        />
                        <PurpleSwitch
                            inputRef={register("allowDoubleVote").ref}
                            name="allowDoubleVote"
                            checked={allowDoubleVoteChecked}
                            onChange={
                                isAdvancedDataCollectionLocked
                                    ? undefined
                                    : handleAllowDoubleVoteToggle
                            }
                            disabled={isAdvancedDataCollectionLocked}
                            size="medium"
                            inputProps={{
                                "aria-label": t(
                                    "dialogs.channel_settings.allow_double_vote_toggle_aria"
                                ),
                            }}
                        />
                    </Grid>
                </LockedSetting>
                {allowDoubleVoteChecked && (
                    <LockedSetting
                        locked={isAdvancedDataCollectionLocked}
                        tooltip={t(UPGRADE_TOOLTIP_KEYS.allowDoubleVote)}
                    >
                        <Box sx={{ mt: 2 }}>
                            <Controller
                                name="doubleVoteCooldownMinutes"
                                control={control}
                                rules={{
                                    min: 1,
                                    max: 10080,
                                }}
                                render={({
                                    field: { value, onChange, onBlur, ref },
                                }) => (
                                    <TextField
                                        label={
                                            <PremiumSettingLabel
                                                label={t(
                                                    "dialogs.channel_settings.double_vote_cooldown_minutes"
                                                )}
                                                locked={
                                                    isAdvancedDataCollectionLocked
                                                }
                                            />
                                        }
                                        type="number"
                                        variant="outlined"
                                        fullWidth
                                        disabled={
                                            isAdvancedDataCollectionLocked
                                        }
                                        value={value ?? 1}
                                        onChange={(event) => {
                                            if (
                                                isAdvancedDataCollectionLocked
                                            ) {
                                                return;
                                            }
                                            const next = event.target.value;
                                            onChange(
                                                next === "" ? "" : Number(next)
                                            );
                                        }}
                                        onBlur={onBlur}
                                        inputRef={ref}
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                                max: 10080,
                                            },
                                        }}
                                        helperText={t(
                                            "dialogs.channel_settings.double_vote_cooldown_helper",
                                            {
                                                value: Math.max(
                                                    1,
                                                    typeof value === "number"
                                                        ? value
                                                        : channelDoubleVoteCooldownMinutes ??
                                                              1
                                                ),
                                            }
                                        )}
                                    />
                                )}
                            />
                        </Box>
                    </LockedSetting>
                )}
            </Grid>
            <Grid item xs={12}>
                <Grid container sx={classes.gridContainer}>
                    <Typography>
                        {t("dialogs.channel_settings.set_voting_time_limit")}
                    </Typography>

                    <Controller
                        name="timer"
                        control={control}
                        rules={{
                            min: 0,
                            max: 720,
                        }}
                        render={({
                            field: { value, onChange, onBlur, ref },
                        }) => (
                            <TextField
                                label={t("dialogs.channel_settings.hours")}
                                type="number"
                                size="small"
                                value={value ?? 0}
                                onChange={(event) => {
                                    const next = event.target.value;
                                    onChange(next === "" ? "" : Number(next));
                                }}
                                onBlur={onBlur}
                                inputRef={ref}
                                InputProps={{
                                    inputProps: {
                                        min: 0,
                                        max: 720,
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </>
    );
};

interface BrandingSectionProps {
    channelId: string;
    channelUsesProfileAsQrLogo?: boolean;
    classes: ChannelSettingsDialogStyles;
    control: Control<ChannelSettingsFormData>;
    handleChangeQRLogo: () => void;
    handleColorChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isBrandingLocked: boolean;
    profileImgPath: string;
    qrColor: string;
    register: UseFormRegister<ChannelSettingsFormData>;
    useChannelProfileAsQrLogoChecked?: boolean;
}

export const BrandingSection: React.FC<BrandingSectionProps> = ({
    channelId,
    channelUsesProfileAsQrLogo,
    classes,
    control,
    handleChangeQRLogo,
    handleColorChange,
    isBrandingLocked,
    profileImgPath,
    qrColor,
    register,
    useChannelProfileAsQrLogoChecked,
}) => {
    const { t } = useTranslation();

    return (
        <>
            <Grid item xs={12}>
                <LockedSetting
                    locked={isBrandingLocked}
                    tooltip={t(UPGRADE_TOOLTIP_KEYS.customQrColor)}
                >
                    <Grid container sx={classes.settingRow}>
                        <Typography sx={classes.qrColorText}>
                            <PremiumSettingLabel
                                label={t("dialogs.channel_settings.custom_qr")}
                                locked={isBrandingLocked}
                            />
                        </Typography>
                        <Box sx={classes.colorWrapper}>
                            <Controller
                                name="qrColor"
                                control={control}
                                render={() => (
                                    <Box sx={classes.picker}>
                                        <TextField
                                            type="color"
                                            value={qrColor}
                                            onChange={
                                                isBrandingLocked
                                                    ? undefined
                                                    : handleColorChange
                                            }
                                            disabled={isBrandingLocked}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                )}
                            />
                            <Box sx={classes.qrCode}>
                                <TendbeQRCode
                                    value={channelId}
                                    profileImgPath={profileImgPath}
                                    qrColor={qrColor}
                                    useChannelProfileAsQrLogo={
                                        channelUsesProfileAsQrLogo
                                    }
                                />
                            </Box>
                        </Box>
                    </Grid>
                </LockedSetting>
            </Grid>
            <Grid item xs={12}>
                <LockedSetting
                    locked={isBrandingLocked}
                    tooltip={t(UPGRADE_TOOLTIP_KEYS.qrLogo)}
                >
                    <Grid container sx={classes.settingRow}>
                        <Typography>
                            <PremiumSettingLabel
                                label={t(
                                    "dialogs.channel_settings.use_channel_profile_as_qr_logo"
                                )}
                                locked={isBrandingLocked}
                            />
                        </Typography>
                        <PurpleSwitch
                            inputRef={register("useChannelProfileAsQrLogo").ref}
                            name="useChannelProfileAsQrLogo"
                            checked={useChannelProfileAsQrLogoChecked}
                            onChange={
                                isBrandingLocked
                                    ? undefined
                                    : handleChangeQRLogo
                            }
                            disabled={isBrandingLocked}
                            size="medium"
                            inputProps={{
                                "aria-label": t(
                                    "dialogs.channel_settings.use_channel_profile_as_qr_logo_toggle_aria"
                                ),
                            }}
                        />
                    </Grid>
                </LockedSetting>
            </Grid>
        </>
    );
};

interface CompletionRedirectSectionProps {
    completionRedirectToggled: boolean;
    control: Control<ChannelSettingsFormData>;
    errors: FieldErrors<ChannelSettingsFormData>;
    handleCompletionRedirectToggle: () => void;
    isBrandingLocked: boolean;
    register: UseFormRegister<ChannelSettingsFormData>;
}

export const CompletionRedirectSection: React.FC<
    CompletionRedirectSectionProps
> = ({
    completionRedirectToggled,
    control,
    errors,
    handleCompletionRedirectToggle,
    isBrandingLocked,
    register,
}) => {
    const { t } = useTranslation();

    return (
        <Grid item xs={12}>
            <LockedSetting
                locked={isBrandingLocked}
                tooltip={t(UPGRADE_TOOLTIP_KEYS.completionRedirect)}
            >
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <PremiumSettingLabel
                        label={t(
                            "dialogs.channel_settings.completion_redirect"
                        )}
                        locked={isBrandingLocked}
                    />
                    <PurpleSwitch
                        inputRef={register("completionRedirectEnabled").ref}
                        name="completionRedirectEnabled"
                        checked={completionRedirectToggled}
                        onChange={
                            isBrandingLocked
                                ? undefined
                                : handleCompletionRedirectToggle
                        }
                        disabled={isBrandingLocked}
                        size="medium"
                        inputProps={{
                            "aria-label": t(
                                "dialogs.channel_settings.completion_redirect_toggle_aria"
                            ),
                        }}
                    />
                </Grid>
            </LockedSetting>
            {completionRedirectToggled && (
                <LockedSetting
                    locked={isBrandingLocked}
                    tooltip={t(UPGRADE_TOOLTIP_KEYS.completionRedirect)}
                >
                    <Box sx={{ mt: 2 }}>
                        <Controller
                            name="completionRedirectUrl"
                            control={control}
                            rules={{
                                // Only enforce a valid URL while the toggle is on.
                                validate: (value, formValues) =>
                                    !formValues.completionRedirectEnabled ||
                                    isBrandingLocked ||
                                    resolveRedirectTarget(value) !== null ||
                                    t(
                                        "dialogs.channel_settings.completion_redirect_url_error"
                                    ),
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    type="url"
                                    label={
                                        <PremiumSettingLabel
                                            label={t(
                                                "dialogs.channel_settings.completion_redirect_url"
                                            )}
                                            locked={isBrandingLocked}
                                        />
                                    }
                                    placeholder="https://"
                                    variant="outlined"
                                    fullWidth
                                    disabled={isBrandingLocked}
                                    value={field.value ?? ""}
                                    error={!!errors.completionRedirectUrl}
                                    inputProps={{
                                        maxLength:
                                            MAX_COMPLETION_REDIRECT_URL_LENGTH,
                                    }}
                                    helperText={
                                        errors.completionRedirectUrl?.message ||
                                        t(
                                            "dialogs.channel_settings.completion_redirect_helper"
                                        )
                                    }
                                />
                            )}
                        />
                    </Box>
                </LockedSetting>
            )}
        </Grid>
    );
};
